import { combineEpics, Epic, ActionsObservable, StateObservable, ofType } from 'redux-observable';
import { RootAction, RootState } from '@src/app/_redux';
import { ApiClient } from '@src/app/_global/utility/apiUtils';
import { ApiConfig } from '@src/app/_global/domain';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { concat } from 'rxjs/observable/concat';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { actionCreators, STOPCHARGE_START } from '@src/app/accounts/stopcharge/actions';
import { actionCreators as weakActionCreators } from '@src/app/login/weak/actions';
import { actionCreators as gaActionCreators } from '@src/app/analytics/googleAnalytics/actions';
import { getLoggedInCustomer } from '@src/app/accounts/selectors';
import { forkJoin } from 'rxjs/observable/forkJoin';

const STOPCHARGE_PATH = '/orderupdate';
const getSeriesUpdateSucceeded = (...responses) => {
  const successResponses = responses.filter(item => 
    item.success === true);
  return successResponses;
};
const formatBody = (stopChargePayload: any) => {
  let body = {};
  if (stopChargePayload.systemCode === 'RESIN') {
    body = {
      prodIdAlias: stopChargePayload.prodIdAlias,
      accountNumber: stopChargePayload.accountNumber,
      systemCode: stopChargePayload.systemCode,
      resinClientId: stopChargePayload.resinClientId,
      resinSubscriptionId: stopChargePayload.resinSubscriptionId,
      resinAutoRenew: stopChargePayload.resinAutoRenew,
      reasonCode: stopChargePayload.reasonCode,
    };
  } else {
    body = {
      prodIdAlias: stopChargePayload.prodIdAlias,
      accountNumber: stopChargePayload.accountNumber,
      systemCode: stopChargePayload.systemCode,
      order: {
        digital: 'Y',
        stopRecurringCharge: 'Y',
        orderNumber: stopChargePayload.orderNumber,
        packageTransaction: stopChargePayload.packageTransaction,
        reasonCode: '1'
      }
    };
  }
  return body;
};
function stopCharge(action$: ActionsObservable<RootAction>,
  state$: StateObservable<RootState>) {
  return action$.pipe(
    ofType(STOPCHARGE_START),
    mergeMap(({ payload }) => {
      const client = new ApiClient();
      const clientConfig = state$.value.clientConfig.clientConfig;
      const loading = of(actionCreators.stopChargeLoading(true));

      const promises = payload.map(stopChargePayload => {
        let body = formatBody(stopChargePayload);
        const apiConfig: ApiConfig = {
          credentialsObject: state$.value.auth.omsAuth.credentials,
          url: clientConfig.url,
          path: STOPCHARGE_PATH,
          region: clientConfig.Region,
          requestmethod: 'PUT',
          requestbody: body,
          parms: {},
          additionalParms: {
            headers: {
              'x-session-id': clientConfig.uuid
            },
          },
        };
        return fromPromise(client.callApiGateway(apiConfig));
      });
      let loggedIncustomer = getLoggedInCustomer(state$.value);
      const refetchCustomerPayload = {
        accountNumber: loggedIncustomer.accountNumber,
        postalCode: loggedIncustomer.postalCode,
      };
      let reloadCust = of(weakActionCreators.refetchCustomer(refetchCustomerPayload));
      const source$ = forkJoin(promises, getSeriesUpdateSucceeded).pipe(
        map(responses => {
          let resumeCount = payload.length;
          if (resumeCount === responses.length) {
            gaActionCreators.createGAEvent({
              category: payload[0].googleAnalyticsCategory,
              action: 'Successful',
              label: payload[0].prodIdAlias
            });
            return actionCreators.stopChargeSuccess({ payload, responses, success: true });
          } else {
            gaActionCreators.createGAEvent({
              category: payload[0].googleAnalyticsCategory,
              action: 'Unsuccessful',
              label: payload[0].prodIdAlias
            });
            return actionCreators.stopChargeError({
              success: false,
              message: 'Error on Stop Charge',
            });
          }
        }),
        catchError(err => {
          gaActionCreators.createGAEvent({
            category: payload[0].googleAnalyticsCategory,
            action: 'Unknown Error',
            label: payload[0].prodIdAlias
          });
          return of(
            actionCreators.stopChargeError({ success: false, message: err })
          );
        })
      );
      return concat(loading, source$, reloadCust);
    })
  );
}
export const epics = combineEpics(stopCharge);
